import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { Table, THead, TH, TBody, TR, TD } from "../components/Table";
import type { components } from "../api/types";

type AuditEvent = components["schemas"]["AuditEvent"];

const KNOWN_TYPES = [
  "",
  "account_deleted",
  "admin.promote",
  "admin.demote",
  "admin.credit_grant",
  "admin.ban",
  "admin.unban",
  "admin.rate_limit_override",
  "admin.cascade_recover",
  "admin.plan_updated",
  "admin.impersonation_started",
];

export function AuditLog() {
  const { data: me } = useUser();
  const [filter, setFilter] = useState("");

  const q = useInfiniteQuery({
    queryKey: ["admin", "audit", filter],
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const query: { cursor?: string; type?: string } = {};
      if (pageParam) query.cursor = String(pageParam);
      if (filter) query.type = filter;
      const { data, error } = await apiClient.GET("/api/v1/admin/audit", {
        params: { query },
      });
      if (error) throw error;
      return data as { events: AuditEvent[]; next_cursor?: string };
    },
    getNextPageParam: (last) => last.next_cursor || undefined,
  });

  if (!me) return null;
  const events = q.data?.pages.flatMap((p) => p.events ?? []) ?? [];

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-12">
        <h1 className="text-display font-medium text-ink-50 tracking-tightest mb-6">
          Audit log
        </h1>
        <div className="mb-4">
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mr-2">
            type
          </label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-ink-950 border border-ink-700 rounded px-2 py-1 text-sm text-ink-100"
          >
            {KNOWN_TYPES.map((t) => (
              <option key={t} value={t}>
                {t || "(all)"}
              </option>
            ))}
          </select>
        </div>
        <Table>
          <THead>
            <TH>occurred_at</TH>
            <TH>type</TH>
            <TH>user_id</TH>
            <TH>detail</TH>
          </THead>
          <TBody>
            {events.map((e) => (
              <TR key={e.event_id}>
                <TD className="font-mono text-[11px] text-ink-400">
                  {e.occurred_at}
                </TD>
                <TD>{e.type}</TD>
                <TD className="font-mono text-[11px] text-ink-300">
                  {(e.user_id ?? "").slice(0, 14)}
                </TD>
                <TD>
                  <code className="text-[11px] text-ink-300">
                    {JSON.stringify(e.detail ?? {})}
                  </code>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {q.hasNextPage && (
          <button
            type="button"
            onClick={() => q.fetchNextPage()}
            className="mt-6 text-xs text-ink-400 hover:text-ink-100"
          >
            Load more →
          </button>
        )}
      </main>
    </div>
  );
}
